/**
 * Select/Dropdown Component
 * Accessible select input with custom styling
 */

import { SelectHTMLAttributes } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { cn } from '@/utils/cn';

export interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  label: string;
  options: SelectOption[];
  error?: string;
  helpText?: string;
  placeholder?: string;
  containerClassName?: string;
}

export default function Select({
  label,
  options,
  error,
  helpText,
  placeholder,
  containerClassName,
  className,
  id,
  disabled,
  required,
  ...props
}: SelectProps) {
  const inputId = id || `select-${label.toLowerCase().replace(/\s+/g, '-')}`;
  const hasError = !!error;

  return (
    <div className={cn('w-full', containerClassName)}>
      {/* Label */}
      <label
        htmlFor={inputId}
        className="block text-sm font-medium text-foreground mb-1.5"
      >
        {label}
        {required && <span className="text-danger ml-1">*</span>}
      </label>

      {/* Select Container */}
      <div className="relative">
        <select
          id={inputId}
          disabled={disabled}
          required={required}
          aria-invalid={hasError}
          aria-describedby={
            hasError ? `${inputId}-error` : helpText ? `${inputId}-help` : undefined
          }
          className={cn(
            // Base styles
            'w-full h-11 px-3 pr-10 rounded-md border appearance-none',
            'text-sm text-foreground bg-input',
            'transition-all duration-200',
            'focus:outline-none focus:ring-3 focus:ring-primary/20',

            // States
            !hasError && 'border-border',
            !hasError && 'focus:border-primary',
            hasError && 'border-danger focus:border-danger focus:ring-danger/20',
            disabled && 'opacity-60 cursor-not-allowed bg-muted',

            // Custom class
            className
          )}
          {...props}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((option) => (
            <option
              key={option.value}
              value={option.value}
              disabled={option.disabled}
            >
              {option.label}
            </option>
          ))}
        </select>

        {/* Chevron Icon */}
        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
          <ChevronDownIcon
            className={cn(
              'w-5 h-5',
              hasError ? 'text-danger' : 'text-foreground/40'
            )}
          />
        </div>
      </div>

      {/* Helper/Error Text */}
      {(error || helpText) && (
        <p
          id={error ? `${inputId}-error` : `${inputId}-help`}
          className={cn(
            'mt-1.5 text-sm',
            error ? 'text-danger' : 'text-foreground/60'
          )}
        >
          {error || helpText}
        </p>
      )}
    </div>
  );
}
